import { defineStore } from "pinia";
import { api } from "@/helpers/api.js";
import { isoCountry2 } from "@xs2event/js-lib";

export const lookupCountryName = (iso_country, locale = "en") => {
  const country = isoCountry2.find((country) => country.code3 === iso_country);

  if (!country) return;

  // "zh_Hans" is not compatible with Inl library
  const parsedLocale = locale == "zh_Hans" ? "zh-Hans" : locale;

  return new Intl.DisplayNames([parsedLocale], { type: "region" }).of(country.code);
};

const uc = (...args) => args.join("|").toUpperCase();

const fetchVenues = () => api.cache("/venues?page_size=9999").then((res) => res.data.venues);

const mapVenues = (data) => {
  const venues = {};
  const cities = {};
  const countries = {};
  for (const venue of data) {
    const COUNTRY = uc(lookupCountryName(venue.country), venue.country);
    const CITY = uc(venue.city, COUNTRY);
    const VENUE = uc(venue.official_name, CITY);
    countries[COUNTRY] = countries[COUNTRY] || {
      isCountry: true,
      country: venue.country,
      text: lookupCountryName(venue.country) + " (" + venue.country + ")",
      info: "",
      uc: COUNTRY,
      refs: 0,
      isVenueType: {},
    };
    countries[COUNTRY].refs++;
    countries[COUNTRY].isVenueType[venue.venue_type] = true;
    cities[CITY] = cities[CITY] || {
      isCity: true,
      city: venue.city,
      text: venue.city + " (" + venue.country + ")",
      info: "",
      uc: CITY,
      refs: 0,
      isVenueType: {},
    };
    cities[CITY].refs++;
    cities[CITY].isVenueType[venue.venue_type] = true;
    venues[VENUE] = {
      isVenueType: { [venue.venue_type]: true },
      isPopular: !!venue.popular_stadium,
      text: venue.official_name,
      info: venue.city + " (" + venue.country + ")",
      official_name: venue.official_name,
      uc: VENUE,
      ...venue,
    };
  }

  const options = [
    ...Object.values(countries), // .filter(country => country.refs > 1),
    ...Object.values(cities), // .filter(city => city.refs > 1),
    ...Object.values(venues),
  ];

  options.sort((a, b) => !a.isCountry - !b.isCountry || !a.isCity - !b.isCity || a.uc.localeCompare(b.uc));

  return options;
};

export const useLocationsStore = defineStore("locations", {
  state: () => ({
    data: null,
    loading: false,
    locations: [],
    countries: [],
    req: null,
  }),
  getters: {
    venues() {
      if (!this.data) {
        this.data = [];
        this.fetch();
      }
      return this.data;
    },
    ready() {
      return this.venues && this.req;
    },
  },
  actions: {
    async fetch() {
      this.loading = true;
      this.req = fetchVenues();
      this.data = mapVenues(await this.req);
      this.loading = false;
    },
    async numeventsbycountry(query, next) {
      if (query.sport_type === "football") query.sport_type = "soccer";
      if (query.sport_type === "") delete query.sport_type;
      const params = new URLSearchParams({
        stats_profile: "events_per_country",
        ...query,
      });
      const req = api.cache("events/statistics?" + params);
      const res = await req;
      next(res);
    },
    async numeventsbycity(query, next) {
      if (query.sport_type === "football") query.sport_type = "soccer";
      if (query.sport_type === "") delete query.sport_type;
      const params = new URLSearchParams({
        stats_profile: "events_per_city",
        ...query,
      });
      const req = api.cache("events/statistics?" + params);
      const res = await req;
      next(res);
    },
    getVenueById(id) {
      for (const venue of this.venues) {
        if (venue.venue_id === id) {
          return venue;
        }
      }
    },
    getVenue(slug) {
      for (const venue of this.venues) {
        if (venue.slug === slug) {
          return venue;
        }
      }
    },
  },
});
